import { useState, useEffect, useCallback } from "react";
import { C, S } from "../customers/theme";
import { getStatementCustomers, getCustomerStatement } from "../lib/statements";
import { getBusinessSettings } from "../lib/businessSettings";
import StatementView from "./StatementView";
import { localISO, todayISO, startOfMonthISO } from "../lib/dates";

const money = (n) => `€${(Number(n) || 0).toFixed(2)}`;
const monthsAgo = (n) => { const d = new Date(); d.setMonth(d.getMonth() - n); return localISO(d); };

const PRESETS = [
  { key: "month", label: "This month", range: () => ({ from: startOfMonthISO(), to: todayISO() }) },
  { key: "3m", label: "Last 3 months", range: () => ({ from: monthsAgo(3), to: todayISO() }) },   // default
  { key: "12m", label: "Last 12 months", range: () => ({ from: monthsAgo(12), to: todayISO() }) },
  { key: "all", label: "All time", range: () => ({ from: null, to: null }) },
  { key: "custom", label: "Custom", range: null },
];

function Kpi({ label, value, sub, color }) {
  return (
    <div style={{ background: C.surface, border: `1px solid ${C.border}`, borderLeft: `3px solid ${color}`, borderRadius: 10, padding: "12px 16px", minWidth: 150, flex: "1 1 150px" }}>
      <div style={{ color: C.textMuted, fontSize: 11, textTransform: "uppercase", letterSpacing: "0.05em", fontWeight: 700 }}>{label}</div>
      <div style={{ fontSize: 22, fontWeight: 800, marginTop: 2 }}>{value}</div>
      {sub && <div style={{ color: C.textMuted, fontSize: 12, marginTop: 2 }}>{sub}</div>}
    </div>
  );
}

export default function StatementManager({ openCustomerId, onOpenHandled }) {
  const [rows, setRows] = useState([]);
  const [kpis, setKpis] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [business, setBusiness] = useState({});
  const [preset, setPreset] = useState("3m");
  const [custom, setCustom] = useState({ from: monthsAgo(3), to: todayISO() });
  const [statement, setStatement] = useState(null);

  const resolvePeriod = useCallback(() => {
    if (preset === "custom") return { from: custom.from || null, to: custom.to || null };
    const p = PRESETS.find((x) => x.key === preset);
    return p && p.range ? p.range() : { from: null, to: null };
  }, [preset, custom]);
  const periodLabel = preset === "custom" ? `${custom.from || "…"} → ${custom.to || "…"}` : (PRESETS.find((p) => p.key === preset)?.label || "");

  const load = useCallback(async () => {
    setLoading(true); setError("");
    try {
      const [res, bs] = await Promise.all([getStatementCustomers(), getBusinessSettings().catch(() => ({}))]);
      setRows(res.data); setKpis(res); setBusiness(bs || {});
    } catch (e) { setError(e.message || "Could not load statements."); }
    finally { setLoading(false); }
  }, []);
  useEffect(() => { load(); }, [load]);

  const openStatement = useCallback(async (customerId) => {
    setError("");
    try { const st = await getCustomerStatement(customerId, resolvePeriod()); if (st) setStatement(st); else setError("Customer not found."); }
    catch (e) { setError(e.message); }
  }, [resolvePeriod]);

  useEffect(() => {
    if (!openCustomerId) return;
    openStatement(openCustomerId).finally(() => onOpenHandled && onOpenHandled());
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [openCustomerId]);

  // Re-fetch the open statement when the period changes.
  useEffect(() => {
    if (statement) openStatement(statement.customer.id);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [preset, custom.from, custom.to]);

  const filtered = rows.filter((r) => `${r.company_name || ""} ${r.customer_number || ""}`.toLowerCase().includes(search.toLowerCase()));

  const periodPicker = (
    <div style={{ display: "flex", gap: 8, alignItems: "center", flexWrap: "wrap" }}>
      <select style={{ ...S.input, width: "auto" }} value={preset} onChange={(e) => setPreset(e.target.value)} aria-label="Statement period">
        {PRESETS.map((p) => <option key={p.key} value={p.key}>{p.label}</option>)}
      </select>
      {preset === "custom" && (<>
        <input type="date" style={{ ...S.input, width: "auto" }} value={custom.from || ""} onChange={(e) => setCustom((c) => ({ ...c, from: e.target.value }))} aria-label="From date" />
        <input type="date" style={{ ...S.input, width: "auto" }} value={custom.to || ""} onChange={(e) => setCustom((c) => ({ ...c, to: e.target.value }))} aria-label="To date" />
      </>)}
    </div>
  );

  return (
    <div>
      <div style={{ display: "flex", gap: 12, marginBottom: 16, flexWrap: "wrap" }}>
        <Kpi label="Total Receivables" value={money(kpis?.totalOutstanding)} sub="all outstanding" color={C.warning} />
        <Kpi label="Customers with a Balance" value={kpis?.customersWithBalance ?? 0} sub={`${kpis?.count ?? 0} active`} color={C.primary} />
        <Kpi label="Overdue" value={money(kpis?.overdueOutstanding)} sub="past due date" color={C.danger} />
      </div>

      <div style={{ display: "flex", gap: 10, marginBottom: 16, flexWrap: "wrap", alignItems: "center" }}>
        <input style={S.searchBar} placeholder="Search customers…" value={search} onChange={(e) => setSearch(e.target.value)} aria-label="Search customers" />
        {periodPicker}
        <div style={{ flex: 1 }} />
        <span style={{ color: C.textMuted, fontSize: 12 }}>{filtered.length} customer{filtered.length === 1 ? "" : "s"}</span>
      </div>

      {error && (
        <div role="alert" style={{ display: "flex", alignItems: "center", gap: 12, background: C.dangerDim, border: `1px solid ${C.danger}`, color: C.text, borderRadius: 8, padding: "10px 14px", marginBottom: 14, fontSize: 13 }}>
          <span style={{ color: C.danger, fontWeight: 700 }}>⚠ {error}</span>
          <button style={S.btn("secondary")} onClick={() => setError("")}>Dismiss</button>
        </div>
      )}

      <div style={S.card}>
        <table style={S.table}>
          <thead><tr>
            <th style={S.th}>Customer</th><th style={S.th}>Number</th><th style={S.th}>Current Outstanding</th><th style={S.th} aria-label="Actions"></th>
          </tr></thead>
          <tbody>
            {loading ? (
              <tr><td colSpan={4} style={S.empty}>Loading…</td></tr>
            ) : filtered.length === 0 ? (
              <tr><td colSpan={4} style={S.empty}>{search ? "No customers match your search." : "No active customers yet."}</td></tr>
            ) : filtered.map((r) => (
              <tr key={r.id} style={{ cursor: "pointer" }} onClick={() => openStatement(r.id)}>
                <td style={S.td}><strong>{r.company_name || "—"}</strong></td>
                <td style={S.td}><span style={{ color: C.textMuted, fontFamily: "monospace", fontSize: 12 }}>{r.customer_number || "—"}</span></td>
                <td style={S.td}><span style={{ color: r.outstanding > 0 ? C.text : (r.outstanding < 0 ? C.success : C.textDim) }}>{money(r.outstanding)}{r.outstanding < 0 ? " CR" : ""}</span></td>
                <td style={S.td}><button style={S.btn("secondary")} onClick={(e) => { e.stopPropagation(); openStatement(r.id); }}>Statement →</button></td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {statement && (
        <StatementView statement={statement} business={business} periodLabel={periodLabel} periodPicker={periodPicker} onClose={() => setStatement(null)} />
      )}
    </div>
  );
}
